import { closeIconV2 } from '@/shared/ui/assets';
import { BIOTRICITY_SUPPORT_ADDRESS } from '@/shared/config';
import { CustomButton } from '@/shared/ui/components';
import { Drawer } from 'antd';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useCommonQuestionsDrawer } from './useCommonQuestionsDrawer';
import type { CommonQuestionsDrawerProps } from './commonQuestionsDrawer.types';

const CommonQuestionsDrawer = ({
  visible,
  title,
  handleClickCloseButton,
}: CommonQuestionsDrawerProps) => {
  const { state, handleScroll } = useCommonQuestionsDrawer();

  return (
    <Drawer
      id="common-questions-drawer-id"
      aria-label={typeof title === 'string' ? title : undefined}
      rootClassName="custom-drawer common-drawer"
      width={504}
      closable={false}
      onClose={handleClickCloseButton}
      open={visible}
    >
      <div
        className={classNames('drawer-title-container', state.isScroll ? '--has-box-shadow' : '')}
      >
        <span className="drawer-title">{title}</span>
        <CustomButton
          id="close-btn"
          style="neutral"
          type="tertiary"
          icon={<ReactSVG src={closeIconV2} />}
          onClick={handleClickCloseButton}
          ariaLabel={t`Close`}
        />
      </div>

      <div className="drawer-body-container" onScroll={handleScroll}>
        <div className="paragraph">
          <div className="title">{t`What is the "Flagged Studies" tab?`}</div>
          <div className="content">
            A <span className="bold-text">{t`"Flagged Studies"`}</span>{' '}
            {t`tab is a tab that displays all ongoing studies experiencing technical issues or pending updates. These may include:`}
            <br />
            <li className="bullet-2">
              <span className="bold-text">{t`Ongoing studies with technical issues`}</span>
              {t` such as low connectivity, bad signal, or improper wear of the device by the patient, which could lead to artifact reports or inconclusive study data.`}
            </li>
            <li className="bullet-2">
              <span className="bold-text">{t`Studies pending data upload`}</span>
              {t` where the device has been offline for more than 6 hours during the upload phase, preventing the study from completing successfully.`}
            </li>
          </div>
        </div>

        <div className="paragraph">
          <div className="title">{t`How would I know which issue a study is experiencing?`}</div>
          <div className="content">
            {t`On each study card in the "Flagged Studies" tab, there are two attributes:`}
            <br />
            <li className="bullet-2">
              <span className="bold-text">{t`Issues found in last 6 hours`}</span>
              {t` - shows what issue(s) the study has encountered in the last 6 hours.`}
            </li>
            <li className="bullet-2">
              <span className="bold-text">{t`Common issue found in the study`}</span>
              {t` - shows the most frequent issue(s) observed during the study.`}
            </li>
            {t`For studies pending data upload, the attributes will display the "Data upload pending due to offline device".`}
          </div>
        </div>
        <div className="paragraph">
          <div className="title">
            {t`How do I find solution(s) to the issue found in the study?`}
          </div>
          <div className="content">
            {t`Each study card in the "Flagged Studies" tab has a `}
            <span className="bold-text">{t`"View solutions"`}</span>
            {t` button. Clicking this button will display step-by-step instructions.`}
            <li className="bullet-2">
              {t`For `}
              <span className="bold-text">{t`artifact-related issues`}</span>
              {t`, the solutions provides troubleshooting steps for improving signal or device wear.`}
            </li>
            <li className="bullet-2">
              {t`For `}
              <span className="bold-text">{t`pending upload issues`}</span>
              {t`, the solutions provides steps to reconnect the device so the data upload can resume.`}
            </li>
            {t`If you cannot resolve the issue, please send a support request to the Biotricity Support Team via `}
            <a
              className="hover-underline-animation"
              href={`mailto:${BIOTRICITY_SUPPORT_ADDRESS.csMail}`}
            >
              {BIOTRICITY_SUPPORT_ADDRESS.csMail}
            </a>
            {t`, and the team will assist you.`}
          </div>
        </div>

        <div className="paragraph">
          <div className="title">{t`What is the "Total Artifact Percentage"?`}</div>
          <div className="content">
            <span className="bold-text">{t`"Total Artifact Percentage"`}</span>{' '}
            {t`is an estimated and calculated percentage based on the number of evaluation strips with bad signals and the duration of the study.`}
          </div>
          <div className="content-2">
            <li className="bullet-1">
              {t`Studies with`} <span className="bold-text">{t`"Total artifact percentage"`}</span>{' '}
              {t` greater than or equal to`} <span className="orange-text">50%</span>{' '}
              {t`shall have an orange label`}{' '}
              <span className="orange-text">{t`“Need attention”`}</span>{' '}
              {t`displayed and will need your attention to review the issue that is found in the study.`}
            </li>
            <li className="bullet-2">
              {t`Studies with`} <span className="bold-text">{t`"Total artifact percentage"`}</span>{' '}
              {t`greater than or equal to`} <span className="red-text">70%</span>{' '}
              {t`shall have a red label`} <span className="red-text">{t`“Potentially lost”`}</span>{' '}
              {t`displayed and it will signify that data of the study will be imperfect.`}
            </li>
          </div>
        </div>

        <div className="paragraph">
          <div className="title">
            {t`What does "Data upload pending due to offline device" mean?`}
          </div>
          <div className="content">
            {t`This issue indicates that the study recording has finished, but the device was offline for more than 6 hours during the data upload process. As a result, the study could not complete successfully, and the device is unavailable for new studies. To resolve this, follow the `}
            <span className="bold-text">{t`"View solutions"`}</span>
            {t` instructions on the study card to restore the connection and complete the upload.`}
          </div>
        </div>

        <div className="paragraph">
          <div className="title">{t`Where can I view flagged studies?`}</div>
          <div className="content">
            {t`You can view flagged studies on the Flagged Studies page or in the Notification Center. The Notification Center shows alerts for:`}
            <li className="bullet-2">
              {t`Studies with technical issues and a "Total Artifact Percentage" ≥ 50%.`}
            </li>
            <li className="bullet-2">
              {t`Studies pending update due to device being offline for more than 6 hours.`}
            </li>
            <span className="bold-text">{t`Please note:`}</span>
            {t` The Notification Center shows alerts for viewing purposes only. To review flagged studies in detail, click the `}
            <span className="bold-text">{t`"View flagged studies"`}</span>
            {t` button at the top of the Notification Center.`}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default CommonQuestionsDrawer;
